// Utility functions for managing site settings

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default features for each plan (used for migration)
const defaultFeatures = [
  // Startup features
  [
    'Up to 1,000 conversations/month',
    'Single industry specialization',
    'Email support',
    'Basic analytics dashboard',
    'Cloud deployment',
    'API access'
  ],
  // SME features
  [
    'Up to 10,000 conversations/month',
    'Multi-industry support',
    'Priority support',
    'Advanced analytics & insights',
    'Cloud or on-premise deployment',
    'Custom integrations',
    'Dedicated account manager'
  ],
  // Enterprise features
  [
    'Unlimited conversations',
    'All industries included',
    '24/7 dedicated support',
    'White-label solution',
    'On-premise deployment',
    'Custom AI training',
    'SLA guarantees',
    'Security audits'
  ]
];

// Default settings
const getDefaultSettings = () => ({
  homeHeroTitle: 'Our AI Expertise Automates Your Operations',
  homeHeroDescription: 'Meet the leading agentic AI platform for process automation. EAET automates workflows with AI-powered Analytics Agents, Marketing Agents, and specialized agents for every business function—helping enterprises reduce operational costs, increase efficiency, and become AI Native.',
  agentsHeroTitle: 'AI Agents',
  agentsHeroDescription: 'Discover 60+ pre-built AI agents ready to automate your workflows. Each agent is specialized for specific tasks and can be deployed in minutes.',
  contactEmail: 'info@eaet.ai',
  contactPhone: '(305) 203-9930',
  showContactInfo: true,
  showChatWidget: true,
  pricingPlans: [
    {
      name: 'Startup',
      price: '$499',
      period: '/month',
      description: 'Perfect for growing businesses',
      features: defaultFeatures[0],
      cta: 'Start Free Trial',
      ctaLink: '/demo',
      popular: false
    },
    {
      name: 'SME',
      price: '$1,999',
      period: '/month',
      description: 'For scaling operations',
      features: defaultFeatures[1],
      cta: 'Book Demo',
      ctaLink: '/demo',
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For large-scale deployments',
      features: defaultFeatures[2],
      cta: 'Contact Sales',
      ctaLink: '/contact',
      popular: false
    }
  ]
});

// Fetch settings from backend
export const getSettings = async () => {
  try {
    const response = await fetch(`${API}/settings`);
    const backendSettings = await response.json();
    
    // If backend has settings, use them
    if (backendSettings && Object.keys(backendSettings).length > 0) {
      // Ensure features exist in pricing plans
      if (backendSettings.pricingPlans && Array.isArray(backendSettings.pricingPlans)) {
        backendSettings.pricingPlans = backendSettings.pricingPlans.map((plan, index) => {
          if (!plan.features || plan.features.length === 0) {
            return {
              ...plan,
              features: defaultFeatures[index] || []
            };
          }
          return plan;
        });
      }
      
      return backendSettings;
    }
  } catch (error) {
    console.warn('Could not fetch settings from backend, using defaults:', error);
  }
  
  // Return defaults if backend fails or has no settings
  return getDefaultSettings();
};

// Synchronous version for initial render (uses cached or defaults)
export const getSettingsSync = () => {
  const cached = sessionStorage.getItem('cachedSettings');
  if (cached) {
    return JSON.parse(cached);
  }
  return getDefaultSettings();
};

// Save settings to backend
export const saveSettings = async (settings) => {
  try {
    const response = await fetch(`${API}/settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings)
    });
    
    if (!response.ok) {
      throw new Error('Failed to save settings');
    }
    
    // Cache in sessionStorage for quick access
    sessionStorage.setItem('cachedSettings', JSON.stringify(settings));
    
    return await response.json();
  } catch (error) {
    console.error('Error saving settings:', error);
    throw error;
  }
};

export const getSetting = (key, defaultValue = '') => {
  const settings = getSettingsSync();
  return settings[key] !== undefined ? settings[key] : defaultValue;
};
